import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { articlesApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import ArticleCard from "../components/ArticleCard";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { PenLine, FileText, Send, FilePen, BookOpen, Wrench, Lightbulb, Search, Coins } from "lucide-react";

export default function Dashboard() {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  useEffect(() => { loadData(); refreshUser?.(); }, []);

  const loadData = async () => {
    try {
      const [a, s] = await Promise.all([articlesApi.list(), articlesApi.stats()]);
      setArticles(a.data); setStats(s.data);
    } catch { toast.error("Error al cargar datos"); }
    finally { setLoading(false); }
  };

  const filteredArticles = articles.filter((a) => {
    const matchesFilter = filter === "all" || a.classification === filter || a.status === filter;
    const matchesSearch = !searchQuery || a.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const statCards = [
    { label: "TOTAL", value: stats?.total || 0, icon: FileText, color: "#00D4FF" },
    { label: "PUBLICADOS", value: stats?.published || 0, icon: Send, color: "#4ADE80" },
    { label: "BORRADORES", value: stats?.drafts || 0, icon: FilePen, color: "#F0A050" },
  ];
  const classCards = [
    { key: "informativo", label: "Informativos", icon: BookOpen, color: "#3B82F6" },
    { key: "practico", label: "Prácticos", icon: Wrench, color: "#10B981" },
    { key: "aplicable", label: "Aplicables", icon: Lightbulb, color: "#F0A050" },
  ];

  return (
    <div data-testid="dashboard-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Hero Banner — gradient + cyan glow to match robot image atmosphere */}
        <div className="relative rounded-2xl overflow-hidden mb-8 animate-fade-in-up"
          style={{
            background: "linear-gradient(135deg, #0F2840 0%, #132B3E 40%, #1A3D5C 70%, #1E4A6E 100%)",
            border: "1px solid rgba(0,188,212,0.25)",
            boxShadow: "0 0 40px rgba(0,188,212,0.08), inset 0 0 60px rgba(0,188,212,0.04)",
          }}>
          {/* Cyan radial glow behind robot to blend with image */}
          <div className="absolute inset-0 hidden md:block" style={{
            background: "radial-gradient(circle at 75% 50%, rgba(0,212,255,0.18) 0%, rgba(0,188,212,0.08) 25%, transparent 55%)",
          }} />
          {/* Desktop: image positioned right, contained, with gradient blend on ALL edges */}
          <img src="/hero-banner.jpg" alt="" className="absolute right-0 top-0 h-full object-contain object-right hidden md:block"
            style={{ maxWidth: "50%" }} />
          <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(to right, #132B3E 0%, rgba(19,43,62,0.92) 35%, rgba(19,43,62,0.6) 48%, rgba(19,43,62,0.2) 60%, transparent 72%)" }} />
          <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(to left, rgba(19,43,62,0.85) 0%, rgba(19,43,62,0.35) 5%, transparent 12%)" }} />
          <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(to bottom, rgba(19,43,62,0.7) 0%, rgba(19,43,62,0.2) 5%, transparent 15%)" }} />
          <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(to top, rgba(19,43,62,0.7) 0%, rgba(19,43,62,0.2) 5%, transparent 15%)" }} />
          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8 lg:p-10 md:min-h-[280px] flex items-center">
            <div className="flex-1 max-w-md">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#E2E8F0" }}>
                Tu Panel
              </h1>
              <p className="text-sm mb-1 leading-relaxed" style={{ color: "#CBD5E1", fontFamily: "'Outfit'" }}>
                Forjamos contenido con tu enfoque,<br className="hidden sm:inline" /> voz y estrategia
              </p>
              <div className="flex flex-col gap-0.5 mb-4 mt-2">
                <span className="text-xs font-light" style={{ color: "#00BCD4", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.08em" }}>
                  Tono y enfoque configurable
                </span>
                <span className="text-xs font-light" style={{ color: "#00BCD4", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.08em" }}>
                  Optimizado para SEO y GEO
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="hero-new-article-btn" onClick={() => navigate("/new")}
                  className="rounded-full px-5 text-sm font-medium"
                  style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                  <PenLine className="w-4 h-4 mr-1.5" />Nuevo Articulo
                </Button>
                <Button data-testid="hero-settings-btn" onClick={() => navigate("/settings")}
                  variant="outline"
                  className="rounded-full px-4 text-sm font-medium"
                  style={{ borderColor: "rgba(0,188,212,0.3)", color: "#00BCD4", background: "rgba(0,188,212,0.05)" }}>
                  Configura tu voz
                </Button>
                {user && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                    style={{ background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)" }}>
                    <Coins className="w-3.5 h-3.5" style={{ color: "#4ADE80" }} />
                    <span className="text-sm font-bold" style={{ color: "#4ADE80", fontFamily: "'Space Grotesk'" }}>
                      {user.credits >= 999000 ? "Ilimitados" : user.credits}
                    </span>
                    <span className="text-xs hidden sm:inline" style={{ color: "#94A3B8" }}>creditos</span>
                  </div>
                )}
              </div>
            </div>
            {/* Mobile robot image - aligned to middle of text block */}
            <div className="w-28 flex-shrink-0 md:hidden self-center -mr-3">
              <img src="/hero-mobile.png" alt="La Forja" className="w-full object-contain"
                style={{ filter: "drop-shadow(0 0 10px rgba(0,212,255,0.1))" }} />
            </div>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" style={{ background: "#0F2035" }} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {statCards.map((s, i) => (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`}
                className={`rounded-xl p-4 border card-glow animate-fade-in-up animate-fade-in-up-delay-${Math.min(i + 1, 4)}`}
                style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                  <span className="text-xs uppercase tracking-wider" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>{s.label}</span>
                </div>
                <div className="text-2xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{s.value}</div>
              </div>
            ))}
            {classCards.map((c, i) => (
              <div key={c.key} data-testid={`stat-${c.key}`}
                className={`rounded-xl p-4 border card-glow animate-fade-in-up animate-fade-in-up-delay-${Math.min(i + 2, 4)}`}
                style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                <div className="flex items-center gap-2 mb-2">
                  <c.icon className="w-4 h-4" style={{ color: c.color }} />
                  <span className="text-xs uppercase tracking-wider" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>{c.label}</span>
                </div>
                <div className="text-2xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{stats?.classification_stats?.[c.key] || 0}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 animate-fade-in-up animate-fade-in-up-delay-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
            <input data-testid="search-articles-input" type="text" placeholder="Buscar artículos..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg text-sm border outline-none transition-all duration-200 focus:border-cyan-500/50"
              style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", fontFamily: "'Outfit'" }} />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="filter-select" className="w-44 h-9 text-sm"
              style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }}>
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borradores</SelectItem>
              <SelectItem value="published">Publicados</SelectItem>
              <SelectItem value="informativo">Informativo</SelectItem>
              <SelectItem value="practico">Práctico</SelectItem>
              <SelectItem value="aplicable">Aplicable</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Articles */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" style={{ background: "#0F2035" }} />)}
          </div>
        ) : filteredArticles.length === 0 ? (
          <div data-testid="empty-state" className="rounded-xl border p-12 text-center animate-fade-in-up"
            style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(0,212,255,0.08)" }}>
              <PenLine className="w-8 h-8" style={{ color: "#00D4FF" }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
              {articles.length === 0 ? "No hay artículos todavía" : "Sin resultados"}
            </h3>
            <p className="text-sm mb-6" style={{ color: "#94A3B8" }}>
              {articles.length === 0 ? "Forja tu primer artículo con IA" : "Prueba con otros filtros"}
            </p>
            {articles.length === 0 && (
              <Button data-testid="create-first-article-btn" onClick={() => navigate("/new")}
                className="rounded-full px-6" style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                <PenLine className="w-4 h-4 mr-2" />Crear primer artículo
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredArticles.map((article, i) => (
              <ArticleCard
                key={article.id}
                article={article}
                index={i}
                onDeleted={(deletedId) => setArticles((prev) => prev.filter((a) => a.id !== deletedId))}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
